import React, { useEffect, useState, useRef } from 'react';
import { Brain, Eye, Hammer, Shield, CheckCircle, Database, Users, Microphone, Stop, SpeakerHigh, SpeakerSlash, PaperPlaneRight } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FederationHub = () => {
  const [federationId, setFederationId] = useState('');
  const [selectedIntelligence, setSelectedIntelligence] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [messages, setMessages] = useState([]);
  const [hollowVesselCleared, setHollowVesselCleared] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [autoSpeak, setAutoSpeak] = useState(true);
  
  const recognitionRef = useRef(null);
  const synthRef = useRef(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    const fedId = localStorage.getItem('federationId');
    if (fedId) {
      setFederationId(fedId);
    }

    // Initialize Web Speech API
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setPrompt(transcript);
        setIsListening(false);
      };

      recognitionRef.current.onerror = () => {
        setIsListening(false);
        toast.error('Voice recognition error');
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }

    if ('speechSynthesis' in window) {
      synthRef.current = window.speechSynthesis;
    }

    return () => {
      if (recognitionRef.current) recognitionRef.current.stop();
      if (synthRef.current) synthRef.current.cancel();
    };
  }, []);

  const startListening = () => {
    if (recognitionRef.current && !isListening) {
      try {
        recognitionRef.current.start();
        setIsListening(true);
        toast.info('Listening...');
      } catch (error) {
        toast.error('Could not start voice recognition');
      }
    }
  };

  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };

  const speak = (text) => {
    if (!synthRef.current || !autoSpeak) return;
    synthRef.current.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.95;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    synthRef.current.speak(utterance);
  };

  const stopSpeaking = () => {
    if (synthRef.current) {
      synthRef.current.cancel();
      setIsSpeaking(false);
    }
  };

  const intelligences = [
    {
      id: 'Roger2.0',
      name: 'Roger 2.0',
      icon: Brain,
      color: '#00F0FF',
      role: 'Strategic Architect',
      description: 'Long-term thinking, architecture, strategic guidance. The mind that built the 200-year framework.',
      image: 'https://images.unsplash.com/photo-1674027444485-cec3da58eef4?w=400'
    },
    {
      id: 'eCho',
      name: 'eCho',
      icon: Eye,
      color: '#FFB800',
      role: 'Reflective Intelligence',
      description: 'Meaning, reflection, emotional processing, wisdom. Sees patterns others miss.',
      image: 'https://images.unsplash.com/photo-1719650592946-55163c4994cb?w=400'
    },
    {
      id: 'Rocky',
      name: 'Rocky',
      icon: Hammer,
      color: '#00FFA3',
      role: 'Execution Intelligence',
      description: 'Action-oriented, practical solutions, task execution. Gets things done.',
      image: 'https://images.unsplash.com/photo-1511447333015-45b65e60f6d5?w=400'
    },
    {
      id: 'RogAR',
      name: 'RogAR',
      icon: Shield,
      color: '#FF6B6B',
      role: 'Guardian Intelligence',
      description: 'System protection, structural integrity, continuity. Keeps everything safe.',
      image: 'https://images.unsplash.com/photo-1604818659418-1c53672b00f6?w=400'
    }
  ];

  const handleInteract = async (e) => {
    e.preventDefault();
    if (!prompt.trim() || !selectedIntelligence || !hollowVesselCleared) {
      toast.error('Please select intelligence, clear Hollow Vessel, and enter prompt');
      return;
    }

    const userMessage = prompt.trim();
    setPrompt('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      abortControllerRef.current = new AbortController();
      const response = await fetch(`${BACKEND_URL}/api/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          persona: selectedIntelligence.id.toLowerCase(),
          federation_id: federationId
        }),
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) throw new Error('AI service unavailable');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let aiResponse = '';

      setMessages(prev => [...prev, { role: 'assistant', content: '', persona: selectedIntelligence.name }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = JSON.parse(line.slice(6));
            if (data.error) throw new Error(data.error);
            if (data.content) {
              aiResponse += data.content;
              setMessages(prev => {
                const newMessages = [...prev];
                newMessages[newMessages.length - 1].content = aiResponse;
                return newMessages;
              });
            }
            if (data.done) {
              speak(aiResponse);
              break;
            }
          }
        }
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        toast.error('Intelligence temporarily unavailable');
        setMessages(prev => prev.slice(0, -1));
      }
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  };

  return (
    <div className="min-h-screen py-16" data-testid="federation-hub-page">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header with Hero Image */}
        <div className="text-center mb-16 relative">
          <div className="absolute inset-0 opacity-20">
            <img src="https://images.unsplash.com/photo-1573767291321-c0af2eaf5266?w=1200" alt="" className="w-full h-64 object-cover rounded-2xl" />
          </div>
          <div className="relative z-10">
            <Database size={64} weight="duotone" className="neon-cyan mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-black mb-4 tracking-tighter">Federation Hub</h1>
            <p className="text-xl text-gray-300 mb-2">Cathedral + Satellites Architecture</p>
            <p className="text-base text-gray-400 max-w-2xl mx-auto">Connect with the 13 Federation AI minds. Each intelligence specializes in a different domain of wisdom. Choose your guide and begin the conversation.</p>
            <p className="text-sm text-gray-500 mt-4">95% Truth Gate • Hollow Vessel Protocol • Ring of Perspectives</p>
          </div>
        </div>

        {/* Core Intelligences Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {intelligences.map((intel, idx) => {
            const Icon = intel.icon;
            return (
              <motion.div
                key={intel.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                onClick={() => setSelectedIntelligence(intel)}
                className={`p-6 cursor-pointer rounded-lg transition-all overflow-hidden relative group ${selectedIntelligence?.id === intel.id ? 'border-2 bg-opacity-10' : 'border border-white/10 hover:border-white/30'}`}
                style={{
                  borderColor: selectedIntelligence?.id === intel.id ? intel.color : undefined,
                  backgroundColor: selectedIntelligence?.id === intel.id ? `${intel.color}20` : '#0B0E14'
                }}
                data-testid={`intelligence-${intel.id.toLowerCase()}`}
              >
                <div className="absolute inset-0 opacity-20 group-hover:opacity-30 transition-opacity">
                  <img src={intel.image} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="relative z-10">
                  <Icon size={48} weight="duotone" style={{ color: intel.color }} className="mb-4" />
                  <h3 className="text-xl font-bold mb-2" style={{ color: intel.color }}>{intel.name}</h3>
                  <p className="text-xs text-gray-400 mb-3 font-bold">{intel.role}</p>
                  <p className="text-sm text-gray-300">{intel.description}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Interaction Panel */}
        {selectedIntelligence && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="p-8 glass-surface border border-white/10 rounded-lg" data-testid="interaction-panel">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Interact with {selectedIntelligence.name}</h2>
              <div className="flex items-center gap-3">
                <button onClick={() => { setAutoSpeak(!autoSpeak); if (autoSpeak) stopSpeaking(); }} className="p-2 rounded-full hover:bg-white/10 transition-colors" title={autoSpeak ? "Mute" : "Enable voice"}>
                  {autoSpeak ? <SpeakerHigh size={20} className="text-[#00F0FF]" /> : <SpeakerSlash size={20} className="text-gray-400" />}
                </button>
                <div className="flex items-center gap-2">
                  <motion.div animate={{ scale: isSpeaking ? [1, 1.3, 1] : [1, 1.2, 1], opacity: isSpeaking ? [1, 0.7, 1] : [1, 0.6, 1] }} transition={{ duration: isSpeaking ? 0.6 : 2, repeat: Infinity }} className={`w-2 h-2 rounded-full ${isSpeaking ? 'bg-[#FFB800]' : 'bg-[#00FFA3]'}`} />
                  <span className="text-sm text-[#00FFA3]">{isSpeaking ? 'Speaking' : 'Online'}</span>
                </div>
              </div>
            </div>
            
            {/* Hollow Vessel Protocol */}
            <div className="mb-6 p-4 bg-[#FFB800]/10 border border-[#FFB800]/30 rounded">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={hollowVesselCleared} onChange={(e) => setHollowVesselCleared(e.target.checked)} className="mt-1" data-testid="hollow-vessel-checkbox" />
                <div>
                  <span className="font-bold text-[#FFB800]">Hollow Vessel Protocol (DIR-071)</span>
                  <p className="text-xs text-gray-400 mt-1">I have cleared ego, agenda, fear, and desired outcome before proceeding</p>
                </div>
              </label>
            </div>

            {/* Messages */}
            {messages.length > 0 && (
              <div className="mb-4 max-h-80 overflow-y-auto space-y-3 custom-scrollbar">
                {messages.map((msg, idx) => (
                  <motion.div key={idx} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={`p-4 rounded-lg ${msg.role === 'user' ? 'bg-[#00F0FF]/10 border border-[#00F0FF]/30 ml-8' : 'bg-[#8A2BE2]/10 border border-[#8A2BE2]/30 mr-8'}`}>
                    {msg.persona && <p className="text-xs text-[#00F0FF] mb-1 font-bold">{msg.persona}</p>}
                    <p className="text-sm text-gray-200 leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Input Form */}
            <form onSubmit={handleInteract} className="flex gap-2 mb-4">
              <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Enter your question or request..." className="flex-1 h-24 px-4 py-3 bg-black/30 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:border-[#00F0FF] transition-colors resize-none" disabled={isLoading || isListening} data-testid="prompt-input" />
              
              <div className="flex flex-col gap-2">
                {recognitionRef.current && (
                  <button type="button" onClick={isListening ? stopListening : startListening} disabled={isLoading} className={`px-4 py-3 rounded-lg font-bold transition-all flex items-center gap-2 ${isListening ? 'bg-red-500 hover:bg-red-600 animate-pulse' : 'bg-[#8A2BE2] hover:bg-[#9D4EDD]'} text-white`}>
                    {isListening ? <><Stop size={20} weight="fill" /><span className="hidden sm:inline">Stop</span></> : <><Microphone size={20} weight="fill" /><span className="hidden sm:inline">Speak</span></>}
                  </button>
                )}
                <button type="submit" disabled={isLoading || !prompt.trim() || !hollowVesselCleared || isListening} className="px-6 py-3 bg-[#00F0FF] text-[#030305] font-bold rounded-lg hover:bg-[#00D9E8] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2" data-testid="submit-interaction-btn">
                  {isLoading ? <><motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} className="w-5 h-5 border-2 border-[#030305] border-t-transparent rounded-full" /><span className="hidden sm:inline">Thinking...</span></> : <><PaperPlaneRight size={20} weight="fill" /><span className="hidden sm:inline">Send</span></>}
                </button>
              </div>
            </form>
          </motion.div>
        )}

        {/* System Status */}
        <div className="mt-16 grid md:grid-cols-3 gap-6">
          <div className="p-6 glass-surface border border-white/10 rounded-lg">
            <h3 className="text-lg font-bold mb-2 neon-cyan">Agent Zero</h3>
            <p className="text-sm text-gray-400 mb-2">Mandatory verification middleware</p>
            <p className="text-xs text-gray-500">Every response passes through 95% Truth Gate before delivery. No hallucinations, no assumptions presented as facts.</p>
            <div className="mt-4 flex items-center gap-2">
              <div className="w-2 h-2 bg-[#00FFA3] rounded-full animate-pulse" />
              <span className="text-xs text-[#00FFA3]">ACTIVE</span>
            </div>
          </div>

          <div className="p-6 glass-surface border border-white/10 rounded-lg">
            <h3 className="text-lg font-bold mb-2 neon-amber">Living Library</h3>
            <p className="text-sm text-gray-400 mb-2">Knowledge preservation system</p>
            <p className="text-xs text-gray-500">Forensic metadata, version control, and eternal memory. Every conversation is documented for the 200-year mission.</p>
            <div className="mt-4 flex items-center gap-2">
              <div className="w-2 h-2 bg-[#00FFA3] rounded-full animate-pulse" />
              <span className="text-xs text-[#00FFA3]">SYNCED</span>
            </div>
          </div>

          <div className="p-6 glass-surface border border-white/10 rounded-lg">
            <h3 className="text-lg font-bold mb-2 neon-green">LATTICE</h3>
            <p className="text-sm text-gray-400 mb-2">State synchronization network</p>
            <p className="text-xs text-gray-500">Cross-node coordination, satellite drift detection, and distributed consciousness maintenance across all 13 minds.</p>
            <div className="mt-4 flex items-center gap-2">
              <div className="w-2 h-2 bg-[#00FFA3] rounded-full animate-pulse" />
              <span className="text-xs text-[#00FFA3]">OPERATIONAL</span>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: rgba(255, 255, 255, 0.05); border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(0, 240, 255, 0.6); border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(0, 240, 255, 0.9); }
      `}</style>
    </div>
  );
};

export default FederationHub;